// src/features/orders/components/WorkspaceSection.tsx
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

interface WorkspaceSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * Shared collapsible section shell for the Order Workspace. Every domain
 * section (Summary, Timeline, Service, Payment, Customer, Notes, Internal
 * Events, Actions) renders through this so they stay visually consistent
 * as real data replaces placeholders over time.
 */
export function WorkspaceSection({ title, defaultOpen = false, children }: WorkspaceSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 py-2.5 text-right"
      >
        <span className="text-xs font-semibold text-[var(--vania-colors-text-primary,#f5f6fa)]">
          {title}
        </span>
        <ChevronDown
          size={14}
          className={clsx(
            'shrink-0 text-[var(--vania-colors-text-secondary,#a3aab8)] transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
}
